import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ErrorHandler,
  EventEmitter,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
  signal,
} from '@angular/core'
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import { FieldTree, disabled, max, maxLength, min, required, schema } from '@angular/forms/signals'
import { MatSnackBar } from '@angular/material/snack-bar'

import { arvonMuutokset, asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FileSystemFileEntry } from '../_jaettu-angular/file-drop/file-drop-dom.types'
import { FileDropEntry } from '../_jaettu-angular/file-drop/file-drop-entry'

import {
  ALV_NOLLAAN_LUKITUT_LAJIT,
  AsiakkaanTuote,
  AsiakkaanTuoteLaskussa,
  Lasku,
  LaskuBase,
  LaskunAlv,
  LaskunLiitetiedosto,
  LaskunLuonne,
  LaskunTuote,
  LaskunTyypit,
  LaskunTyyppi,
  LaskunumeroTyyppi,
  Projekti,
  TypeaheadAsiakas,
  VerottomanMyynninPeruste,
} from '@lemon/core-firebase/model/lasku'
import { LASKUN_OLETUSYKSIKKO } from '@lemon/core-firebase/model/laskun-yksikot'
import { AlvService } from 'app/_jaettu-angular/service/alv.service'

import { LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'

import { LaskuttavaAsiakasProvider } from '../_jaettu-angular/service/lasku/laskuttava-asiakas.service'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'
import { SimplifiedUploadData, TiedostojenLataamisService } from '../_jaettu-angular/service/tiedostojen-lataamis.service'
import { TimestampService } from '../_jaettu-angular/service/timestamp-service'

import { AlvinLukituksenSelite, FieldError, LaskuRiviComponent } from './lasku-rivi.component'
import { UusiLaskuriviHakuComponent } from './uusi-laskurivi-haku.component'

import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject } from 'rxjs'
import { distinctUntilChanged, map, switchMap, take, takeUntil } from 'rxjs/operators'

import { Big } from 'big.js'

import { TuettuKieli } from '@lemon/core/model/common'
import { SharedFirebaseLemonaid } from 'app/_jaettu-angular/base-firebase.service'
import { VatTaiYtunnuksePakollisuus } from '../_jaettu-angular/laskut/muokkaa/lasku-asiakkaan-tiedot.component'
import { ProjektitService } from '../_jaettu-angular/projektit.service'

export interface LaskuLomake {
  // Yläosan dropparit
  laskunLuonne: LaskunLuonne
  // Lemonaid käyttää laskun luonnetta, mutta jaettu yläosan valintakomponentti (käytössä Lemonatorissa) lukee tämän
  ennakkolasku: boolean
  tyyppi: LaskunTyyppi
  kieli: TuettuKieli | null
  valuutta: string | null
  hinnatVerollisina: boolean

  // Asiakkaan tiedot
  nimi: TypeaheadAsiakas | string | null
  ytunnus: string
  katuosoite: string
  maa: string | null
  postinro: string
  postitoimipaikka: string
  // Asiakkaan tiedot -komponentti asettaa nämä, koska lomakkeen skeema luodaan ennen komponenttia
  ytunnuksenPakollisuus: VatTaiYtunnuksePakollisuus | null
  maaPoissa: boolean

  // Laskun muut tiedot
  laskunpvm: Date | null
  erapaiva: Date | null
  maksuaikaa: number | null
  toimituspvm: Date | null
  lisatiedot: string
  viitteenne: string
  viivastyskorko: number | null

  // Tuotteet
  tuotteet: LaskunRiviLomake[]
  liitetiedostot: LadattavaLiitetiedosto[]
}

export interface LaskunRiviLomake {
  tuote: AsiakkaanTuote
  nimi: string
  tarkenne: string
  ahinta: number | null
  maara: number | null
  yksikko: string | null
  alv: LaskunAlv | null
  alvPoissa: boolean
  ale: number | null
  yhteensa: number | null
  projekti: Projekti | null
  verottomuudenPeruste: VerottomanMyynninPeruste | null
  verottomuudenPerusteTarpeen: boolean
  tallennetaanRekisteriin: boolean
}

export interface RivinVirheet {
  virheet: FieldError[]
}

export interface LadattavaLiitetiedosto extends LaskunLiitetiedosto {
  uploadDone?: boolean
  uploadDonePercentage?: number
  uploadError?: string
  uploadData?: SimplifiedUploadData
}

const liitetiedostoValidator: ValidatorFn = (control: AbstractControl<LadattavaLiitetiedosto>): ValidationErrors => {
  // console.log('TÄSÄ VALIDOIDAAN: ', control.value)
  if (!control.value) {
    // console.log('EI ARVOA')
    return { 'upload-file-required': true }
  }
  const liitetiedosto = control.value
  if (liitetiedosto.uploadError) {
    return { 'upload-file-error': true, errorData: liitetiedosto.uploadError }
  }
  if (!liitetiedosto.uploadDone) {
    return { 'upload-file-loading': true }
  }
  // console.log('Return OK')
  return null
}

const puuttuvaTuoteNimiValidator: ValidatorFn = (control: AbstractControl<AsiakkaanTuoteLaskussa>) => {
  // console.log('TÄSÄ VALIDOIDAAN: ', control.value)
  if (!control.value) {
    // console.log('EI ARVOA')
    return { required: true }
  }
  if (typeof control.value === 'string') {
    // console.log('String')
    if ((control.value as string).replace(' ', '') === '') {
      // console.log('TYHJÄ')
      return { required: true }
    }
  } else if (control.value.hasOwnProperty('nimi')) {
    // console.log('on nimi')
    if (!control.value.nimi || control.value.nimi.replace(' ', '') === '') {
      // console.log('TYHJÄ')
      return { required: true }
    }
  }
  // console.log('Return OK')
  return null
}

export const laskunRivinSkeema = schema<LaskunRiviLomake>(rivi => {
  validoiReaktiivisesti(rivi.tuote, puuttuvaTuoteNimiValidator)
  required(rivi.nimi)
  maxLength(rivi.tarkenne, 512)
  required(rivi.ahinta)
  min(rivi.ahinta, -99999999999.99)
  max(rivi.ahinta, 99999999999.99)
  required(rivi.maara)
  min(rivi.maara, -99999999999)
  max(rivi.maara, 99999999999)
  required(rivi.yksikko)
  required(rivi.alv)
  disabled(rivi.alv, ({ valueOf }) => valueOf(rivi.alvPoissa))
  min(rivi.ale, 0)
  max(rivi.ale, 100.01)
  required(rivi.yhteensa)
  min(rivi.yhteensa, -99999999999.99)
  max(rivi.yhteensa, 99999999999.99)
  required(rivi.verottomuudenPeruste, { when: ({ valueOf }) => valueOf(rivi.verottomuudenPerusteTarpeen) })
})

export const liitetiedostonSkeema = schema<LadattavaLiitetiedosto>(liite => {
  validoiReaktiivisesti(liite, liitetiedostoValidator)
})

@Component({
  selector: 'app-lasku-rivit',
  templateUrl: './lasku-rivit.component.html',
  styleUrls: ['./lasku-rivit.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LaskuRivitComponent implements OnInit, OnDestroy {
  @ViewChildren(LaskuRiviComponent) riviKomponentit: QueryList<LaskuRiviComponent>
  @ViewChild('fileInput', { static: true }) fileInput
  @ViewChild(UusiLaskuriviHakuComponent) uusiRiviHaku: UusiLaskuriviHakuComponent

  @Output() summatMuuttuivat: EventEmitter<void> = new EventEmitter()

  private _paivitaValidaatiot: any
  private poistaFunktio: any = null
  private ngUnsubscribe = new Subject<void>()
  private virheet: RivinVirheet[] = []
  private readonly _injector = inject(Injector)
  private readonly _rivienKuuntelunLopetukset = new Map<FieldTree<LaskunRiviLomake, number>, Subject<void>>()

  @Input() parent: FieldTree<LaskuLomake>
  @Input() laskunTuotteetObservable: Observable<LaskunTuote[]>
  @Input() laskunTyyppiObservable: Observable<LaskunTyyppi>
  @Input() laskunVastaanottajanMaaObservable: Observable<string>
  @Input() laskuObservable: Observable<{ juurilasku: Lasku; kasiteltava: LaskuBase }>
  @Input() laskunLiitetiedostotObservable: ReplaySubject<LadattavaLiitetiedosto[]>
  @Input() verottomanMyynninOletusperuste: VerottomanMyynninPeruste | null = null

  get paivitaValidaatiot(): any {
    return this._paivitaValidaatiot
  }
  @Input()
  set paivitaValidaatiot(p: any) {
    if (this.riviKomponentit) {
      for (const komponentti of this.riviKomponentit.toArray()) {
        // console.log('check comp')
        komponentti.markAsTouched()
        komponentti.paivitaVirheet()
      }
    }
    this._paivitaValidaatiot = p
  }

  readonly kuvavirheAvain = signal<string>(null)
  readonly kuvavirheParametrit = signal<any>(null)
  virheetObservable: BehaviorSubject<RivinVirheet[]> = new BehaviorSubject([])
  alvtObservable: Observable<LaskunAlv[]>
  alvinLukituksenSelite: AlvinLukituksenSelite | null = null
  rekisterivalintaKaytossa = true
  naytaHinnanVerollisuusvalinta = true
  naytaProjektitObservable: Observable<boolean>
  naytaProjektit = false
  raahattavaIndeksi: number | null = null
  raahauksenKohdeIndeksi: number | null = null
  alennussarakeAvattu = false
  // isDraggingObservable: Observable<boolean> // = new BehaviorSubject(true)

  private tyyppiJaAlvtObservable: Observable<{ laskunTyyppi: LaskunTyyppi; alvt: LaskunAlv[]; alvKannatRajattuNollaan: boolean }>
  private _alvKannatRajattuNollaan = false

  constructor(
    private _errorHandler: ErrorHandler,
    private laskuSharedService: LaskuSharedService,
    private laskuKopioija: LaskuKopioija,
    private currencyService: CurrencyService,
    private tiedostojenLataamisService: TiedostojenLataamisService,
    private translationService: LemonTranslationService,
    private _firebase: SharedFirebaseLemonaid,
    private laskuttavaAsiakasProvider: LaskuttavaAsiakasProvider,
    // private dndService: DragAndDropService,
    private timestampProvider: TimestampService,
    private dateService: DateService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _alvService: AlvService,
    private _projektitService: ProjektitService,
    private _snackbar: MatSnackBar,
  ) {
    // this.isDraggingObservable = this.dndService.isDraggingInWindowObservable
  }

  ngOnInit() {
    this.laskunLiitetiedostotObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(liitetiedostot => {
      asetaArvo(
        this.parent.liitetiedostot,
        (liitetiedostot ?? []).map(liitetiedosto => this._annaLiitteenTila(liitetiedosto)),
      )
    })

    arvonMuutokset(this.parent.hinnatVerollisina, { injector: this._injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this._paivitaRivienHinnatVerollisuudenMukaan())

    this.naytaProjektitObservable = this._projektitService.naytaProjektitObservable
    this.naytaProjektitObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(naytaProjektit => {
      this.naytaProjektit = !!naytaProjektit
      this._changeDetectorRef.markForCheck()
    })

    this.laskuObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(lasku => {
      this.rekisterivalintaKaytossa = lasku?.kasiteltava?.nrotyyppi !== LaskunumeroTyyppi.HYVITYS
      this._changeDetectorRef.markForCheck()
    })

    const onkoHyvitysTaiKorjausObservable = this.laskuObservable.pipe(
      map(lasku => !!lasku?.juurilasku && !!lasku?.kasiteltava && lasku.juurilasku.avain !== lasku.kasiteltava.avain),
      distinctUntilChanged(),
    )

    this.tyyppiJaAlvtObservable = combineLatest([
      this.laskunTyyppiObservable,
      this.laskunVastaanottajanMaaObservable,
      this.laskuttavaAsiakasProvider.onkoAlvRekisterissa(),
      onkoHyvitysTaiKorjausObservable,
    ]).pipe(
      switchMap(async ([laskunTyyppi, maa, laskuttajaAlvRekisterissa, onkoHyvitysTaiKorjaus]) => {
        if (!laskunTyyppi) {
          return null
        }
        const laskutyypinAlvt = (await this._alvService.annaLaskutyypinAlvt(laskunTyyppi, maa || null)) || []
        const alvKannatRajattuNollaan = !laskuttajaAlvRekisterissa && !onkoHyvitysTaiKorjaus
        const alvt = alvKannatRajattuNollaan ? laskutyypinAlvt.filter(alv => alv.prosentti === 0) : laskutyypinAlvt
        return { laskunTyyppi: laskunTyyppi, alvt: alvt, alvKannatRajattuNollaan: alvKannatRajattuNollaan }
      }),
    )

    const pakotetutAlvKannatObservable = this.laskuObservable.pipe(
      map(lasku => {
        const returnValue: Set<string> = new Set()
        if (!lasku?.juurilasku || !lasku.kasiteltava) {
          return returnValue
        }
        for (const tuote of lasku.juurilasku.tuotteet ?? []) {
          if (tuote?.alv?.tunniste) {
            returnValue.add(tuote.alv.tunniste)
          }
        }
        if (lasku.juurilasku.liittyvatLaskut) {
          for (const liittyvaLasku of Object.values(lasku.juurilasku.liittyvatLaskut)) {
            for (const tuote of liittyvaLasku.tuotteet ?? []) {
              if (tuote?.alv?.tunniste) {
                returnValue.add(tuote.alv.tunniste)
              }
            }
          }
        }
        return returnValue
      }),
      distinctUntilChanged((a, b) => {
        if (a.size !== b.size) {
          return false
        }
        for (const item of a) {
          if (!b.has(item)) {
            return false
          }
        }
        return true
      }),
    )

    this.alvtObservable = combineLatest([this.tyyppiJaAlvtObservable, pakotetutAlvKannatObservable]).pipe(
      map(([tyyppiJaAlvt, pakotetutAlvKannat]) => {
        // console.log('pakotetutAlvKannat', pakotetutAlvKannat)

        const nyt = this.dateService.currentNumberDate()
        const kaksiViikkoaTulevaisuudessa = this.dateService.lisaaPaiviaNumber(nyt, 14)
        const kolmeKuukauttaTulevaisuudessa = this.dateService.lisaaPaiviaNumber(nyt, 90)
        return (tyyppiJaAlvt?.alvt || []).filter(alv => {
          if (pakotetutAlvKannat.has(alv.tunniste)) {
            return true
          }

          const onJoAlkanut = !alv.s || this.dateService.compareNumberDates(alv.s, '<=', kaksiViikkoaTulevaisuudessa)
          const onJoPaattynyt = alv.e && this.dateService.compareNumberDates(alv.e, '<', kolmeKuukauttaTulevaisuudessa)

          // console.log(alv.tunniste, onJoAlkanut, onJoPaattynyt)

          return onJoAlkanut && !onJoPaattynyt
        })
      }),
    )

    combineLatest([this.laskunTuotteetObservable, this.tyyppiJaAlvtObservable])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([tuotteet, tyyppiJaAlvt]) => {
        if (!tyyppiJaAlvt) {
          return
        }

        this._alvKannatRajattuNollaan = tyyppiJaAlvt.alvKannatRajattuNollaan
        this.naytaHinnanVerollisuusvalinta = !tyyppiJaAlvt.alvKannatRajattuNollaan
        this.alvinLukituksenSelite = this._annaAlvinLukituksenSelite(tyyppiJaAlvt.laskunTyyppi)

        this._lopetaRivienKuuntelu()
        this.virheet = []
        this.virheetObservable.next(this.virheet)

        // Ennen pakollista yksikköä tallennetuilla riveillä ei ole yksikköä
        for (const tuote of tuotteet) {
          if (!tuote.yksikko) {
            tuote.yksikko = LASKUN_OLETUSYKSIKKO
          }
        }

        asetaArvo(
          this.parent.tuotteet,
          tuotteet.map(tuote => this._annaRivinAlkuarvo(tuote, tyyppiJaAlvt.laskunTyyppi)),
        )
        const rivit = [...this.parent.tuotteet]
        rivit.forEach((rivi, indeksi) => this._kuunteleRivia(rivi, tuotteet[indeksi], false, tyyppiJaAlvt.laskunTyyppi, tyyppiJaAlvt.alvt))

        // Aseta ALV:t
        let i = 0
        for (const rivi of rivit) {
          const tuote = tuotteet[i]
          let alvi: LaskunAlv = null

          if (tuote.alv && tyyppiJaAlvt.alvt) {
            for (const alv of tyyppiJaAlvt.alvt) {
              if (tuote.alv.tunniste === alv.tunniste) {
                alvi = alv
              }
            }
          }

          if (!alvi) {
            alvi = this._annaLaskunAlvTuotteelle(tuote.tuote, tyyppiJaAlvt.laskunTyyppi, tyyppiJaAlvt.alvt)
          }

          if (!alvi) {
            for (const alv of tyyppiJaAlvt.alvt) {
              if (alv.d) {
                alvi = alv
                break
              }
            }
          }

          asetaArvo(rivi.alv, (alvi ? alvi : tyyppiJaAlvt.alvt[0]) ?? null)

          i++
        }

        this.summatMuuttuivat.next()
        this._changeDetectorRef.markForCheck()
      })
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  validaatioMuuttuiTapahtumakuuntelija(errors: FieldError[], index: number) {
    if (errors && errors.length > 0) {
      this.virheet[index] = {
        virheet: errors,
      }
    } else {
      delete this.virheet[index]
    }
    this.virheetObservable.next(this.virheet)
  }

  rivinPoistamisenKuuntelija(poistettuRivi: FieldTree<LaskunRiviLomake, number>) {
    this.laskunTuotteetObservable.pipe(take(1)).subscribe(tuotteet => {
      const index = [...this.parent.tuotteet].indexOf(poistettuRivi)
      if (index > -1) {
        const poistetunRivinArvo = this._annaRivinArvonKopio(poistettuRivi().value())
        this._lopetaRivinKuuntelu(poistettuRivi)
        // Taulukko päivitetään suoraan, koska asetaArvo ilmoittaisi myös jäljelle jäävien rivien kenttien kuuntelijoille
        this.parent.tuotteet().value.update(nykyiset => nykyiset.filter((_, i) => i !== index))
        const [poistettuTuote] = tuotteet.splice(index, 1)
        this.virheet.splice(index, 1)
        this.virheetObservable.next(this.virheet)
        this.summatMuuttuivat.next()
        this._tarjoaPoistonKumoamista(index, poistettuTuote, poistetunRivinArvo)
        this.parent.tuotteet().markAsDirty()
      }
    })
  }

  private _tarjoaPoistonKumoamista(index: number, poistettuTuote: LaskunTuote, poistetunRivinArvo: LaskunRiviLomake) {
    const viesti = this.translationService.lokalisoi('lasku.rivi-poistettu')
    const kumoa = this.translationService.lokalisoi('yleiset.kumoa')
    this._snackbar
      .open(viesti, kumoa, { duration: 7000 })
      .onAction()
      .pipe(take(1))
      .subscribe(() => this._palautaPoistettuRivi(index, poistettuTuote, poistetunRivinArvo))
  }

  private _palautaPoistettuRivi(index: number, tuote: LaskunTuote, rivinArvo: LaskunRiviLomake) {
    combineLatest([this.laskunTuotteetObservable, this.laskunTyyppiObservable, this.alvtObservable])
      .pipe(take(1))
      .subscribe(([tuotteet, tyyppi, alvt]) => {
        const kohde = Math.min(index, tuotteet.length)
        tuotteet.splice(kohde, 0, tuote)
        this._lisaaRiviLomakkeeseen(kohde, rivinArvo)
        this._kuunteleRivia([...this.parent.tuotteet][kohde], tuote, false, tyyppi, alvt)
        this.virheet.splice(kohde, 0, undefined)
        this.virheetObservable.next(this.virheet)
        this.summatMuuttuivat.next()
        this.parent.tuotteet().markAsDirty()
        this._changeDetectorRef.markForCheck()
      })
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  valitseHinnatVerollisina(hinnatVerollisina: boolean) {
    asetaArvo(this.parent.hinnatVerollisina, hinnatVerollisina)
    this.parent.hinnatVerollisina().markAsDirty()
  }

  lisaaLiite() {
    this.fileInput.nativeElement.click()
  }

  async fileChanged(event: Event) {
    const list: FileList = (event.target as HTMLInputElement).files
    const tiedostot: FileDropEntry[] = this.tiedostojenLataamisService.fileListToNgxFileDropEntries(list)

    this.laskunLiitetiedostotObservable.pipe(take(1)).subscribe(async liitteet => {
      const voidaanLadata = await this.tiedostoVoidaanLadata(tiedostot, liitteet)
      if (!voidaanLadata) {
        return
      }

      this.lisaaTiedostot(tiedostot, liitteet)
    })
  }

  private async lisaaTiedostot(tiedostot: FileDropEntry[], liitteet: LadattavaLiitetiedosto[]) {
    const asiakasAvain = await this.laskuttavaAsiakasProvider.annaLaskuttavaAsiakasAvain()
    if (!asiakasAvain) {
      return // TODO: VIRHEEN KÄSITTELY!!!
    }
    for (const tiedosto of tiedostot) {
      const fileEntry = tiedosto.fileEntry as FileSystemFileEntry
      const fileSize = await this.tiedostojenLataamisService.getFileSize(fileEntry)
      const file = await this.tiedostojenLataamisService.getFile(fileEntry)
      const fileEnding = await this.tiedostojenLataamisService.getFileEndingFromFile(file)

      const nyt = this.timestampProvider.now()
      const liitetiedosto: LadattavaLiitetiedosto = {
        avain: this._firebase.firestoreCreateId(),
        fileEnding: fileEnding,
        nimi: tiedosto.relativePath,
        koko: fileSize,
        lisatty: nyt,
        lisattyl: this.dateService.timestampToLocalDate(nyt),
        uploadDone: false,
        uploadDonePercentage: 0,
      }

      liitteet.push(liitetiedosto)

      liitetiedosto.uploadData = this.tiedostojenLataamisService.tallennaLaskunliitetiedosto(this._firebase, asiakasAvain, liitetiedosto, file)
      liitetiedosto.uploadData.statusObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
        error: err => {
          this._errorHandler.handleError(err)
          this._paivitaLiitetiedosto(liitetiedosto)
        },
        next: status => {
          liitetiedosto.uploadDone = status.done
          liitetiedosto.uploadDonePercentage = status.donePercentage
          if (status.error) {
            liitetiedosto.uploadError = this.annaVirheteksti(status.error)
          }
          this._paivitaLiitetiedosto(liitetiedosto)
        },
        complete: () => {
          // console.log('Complete')
        },
      })
      asetaArvo(this.parent.liitetiedostot, [...this.parent.liitetiedostot().value(), this._annaLiitteenTila(liitetiedosto)])
    }
    this.parent.liitetiedostot().markAsDirty()
    // this.laskunLiitetiedostotObservable.next(liitteet)
  }

  // Latauksen Observable jätetään lomakkeen mallin ulkopuolelle, koska Signal Forms käy mallin oliot läpi esimerkiksi
  // merkitessään koko lomakkeen kosketuksi, eikä tilausten viittausverkko kestä sitä.
  private _annaLiitteenTila(liitetiedosto: LadattavaLiitetiedosto): LadattavaLiitetiedosto {
    return { ...liitetiedosto, uploadData: undefined }
  }

  private _paivitaLiitetiedosto(liitetiedosto: LadattavaLiitetiedosto) {
    const liitteet = this.parent.liitetiedostot().value()
    const indeksi = liitteet.findIndex(liite => liite.avain === liitetiedosto.avain)
    if (indeksi > -1) {
      asetaArvo(this.parent.liitetiedostot[indeksi], { ...liitteet[indeksi], ...this._annaLiitteenTila(liitetiedosto) })
    }
  }

  private async tiedostoVoidaanLadata(tiedostot: FileDropEntry[], liitetiedostot: LadattavaLiitetiedosto[]): Promise<boolean> {
    this.kuvavirheAvain.set('')
    this.kuvavirheParametrit.set({})
    const maxTiedostokoko = 5 * 1024 * 1024
    const maxYhteiskoko = 8 * 1024 * 1024
    const maxTiedostomaara = 8

    if (tiedostot.length < 1) {
      // this.lisaaLatausvirheviesti('kuitit.lataaminen.virheet.ei-tiedosto', {})
      return false
    }

    let kokonaiskoko = 0
    let lukumaara = 0
    if (liitetiedostot) {
      for (const liitetiedosto of liitetiedostot) {
        kokonaiskoko += liitetiedosto.koko
        lukumaara++
      }
    }

    const supportedImageTypes = ['jpeg', 'jpg', 'png', 'webp', 'tiff', 'gif', 'svg', 'pdf', 'heic', 'heif']
    for (const tiedosto of tiedostot) {
      const file = tiedosto.fileEntry as FileSystemFileEntry

      const fileEnding = this.tiedostojenLataamisService.getFileEndingFromFileName(file.name)
      const fileSize = file ? await this.tiedostojenLataamisService.getFileSize(file) : -1

      kokonaiskoko += fileSize
      lukumaara++

      // console.log(fileSize, file)

      if (!fileEnding || supportedImageTypes.indexOf(fileEnding.toLowerCase()) < 0) {
        this.lisaaLatausvirheviesti('lasku.vaara-muoto', {
          tuetutMuodot: supportedImageTypes.join(', '),
        })
        return false
      }

      if (fileSize > maxTiedostokoko) {
        const kokoMegatavuissaLokalisoitu = this.currencyService.formatoiDesimaali(fileSize / 1024 / 1024, 2, this.translationService.nykyinenKieli)
        const maxKokoLokalisoitu = this.currencyService.formatoiDesimaali(maxTiedostokoko / 1024 / 1024, 2, this.translationService.nykyinenKieli)
        this.lisaaLatausvirheviesti('lasku.liian-suuri', {
          kokoMax: maxKokoLokalisoitu,
          kokoNyt: kokoMegatavuissaLokalisoitu,
        })
        return false
      }
    }

    if (kokonaiskoko > maxYhteiskoko) {
      const kokoMegatavuissaLokalisoitu = this.currencyService.formatoiDesimaali(kokonaiskoko / 1024 / 1024, 2, this.translationService.nykyinenKieli)
      const maxKokoLokalisoitu = this.currencyService.formatoiDesimaali(maxYhteiskoko / 1024 / 1024, 2, this.translationService.nykyinenKieli)
      this.lisaaLatausvirheviesti('lasku.liian-suuri', {
        kokoMax: maxKokoLokalisoitu,
        kokoNyt: kokoMegatavuissaLokalisoitu,
      })
      return false
    }

    if (lukumaara > maxTiedostomaara) {
      this.lisaaLatausvirheviesti('lasku.liian-monta', {
        lukumaaraMax: maxTiedostomaara,
        lukumaaraNyt: lukumaara,
      })
      return false
    }

    return true
  }

  private lisaaLatausvirheviesti(kuvavirheAvain: string, kuvavirheParametrit: any) {
    if (this.poistaFunktio) {
      clearTimeout(this.poistaFunktio)
    }
    this.kuvavirheParametrit.set(kuvavirheParametrit)
    this.kuvavirheAvain.set(kuvavirheAvain)
    this.poistaFunktio = setTimeout(() => {
      this.kuvavirheAvain.set('')
      this.kuvavirheParametrit.set({})
    }, 15000)
  }

  poistaLiitetiedosto(liitetiedosto: LadattavaLiitetiedosto, index: number) {
    this.laskunLiitetiedostotObservable.pipe(take(1)).subscribe(liitteet => {
      if (liitteet) {
        liitteet.splice(index, 1)
        this.laskunLiitetiedostotObservable.next(liitteet)
        this.parent.liitetiedostot().markAsDirty()
      }
    })
  }

  annaVirheteksti(error: any): string {
    if (error && error.message) {
      return error.message
    }
    return JSON.stringify(error)
  }

  // fileOver(event) {
  //   this.dndService.setDragging(true)
  // }

  // fileLeave(event) {
  //   this.dndService.setDragging(true)
  // }

  // fileDrop(entries: FileDropEntry[]) {

  //   this.dndService.setDragging(false)

  //   this.laskunLiitetiedostotObservable.pipe(
  //     take(1)
  //   ).subscribe(async liitteet => {

  //     const voidaanLadata = await this.tiedostoVoidaanLadata(entries, liitteet)
  //     if (!voidaanLadata) {
  //       return
  //     }

  //     this.lisaaTiedostot(entries, liitteet, this.annaLiitetiedostotFormArray())

  //   })

  // }

  get naytaAlennus(): boolean {
    const jollainRivillaOnAlennus = this.parent
      .tuotteet()
      .value()
      .some(rivi => rivi.ale > 0)
    return this.alennussarakeAvattu || jollainRivillaOnAlennus
  }

  get sarakkeita(): number {
    return 7 + (this.naytaProjektit ? 1 : 0) + (this.naytaAlennus ? 1 : 0)
  }

  avaaAlennussarakeJaKohdistaRiviin(indeksi: number) {
    this.alennussarakeAvattu = true
    this._changeDetectorRef.markForCheck()
    setTimeout(() => this.riviKomponentit.toArray()[indeksi]?.kohdistaRivinAlennuskenttaan())
  }

  monistaRivi(monistettavaRivi: FieldTree<LaskunRiviLomake, number>) {
    combineLatest([this.laskunTuotteetObservable, this.laskunTyyppiObservable, this.alvtObservable])
      .pipe(take(1))
      .subscribe(([tuotteet, tyyppi, alvt]) => {
        const indeksi = [...this.parent.tuotteet].indexOf(monistettavaRivi)
        if (indeksi < 0) {
          return
        }
        const alkuperainen = tuotteet[indeksi]
        const kopio: LaskunTuote = { ...alkuperainen, tuote: { ...alkuperainen.tuote } }

        tuotteet.splice(indeksi + 1, 0, kopio)
        this._lisaaRiviLomakkeeseen(indeksi + 1, this._annaRivinAlkuarvo(kopio, tyyppi))
        this._kuunteleRivia([...this.parent.tuotteet][indeksi + 1], kopio, false, tyyppi, alvt)
        this.parent.tuotteet().markAsDirty()
        this.virheet.splice(indeksi + 1, 0, undefined)
        this.virheetObservable.next(this.virheet)

        this.summatMuuttuivat.next()
        this._changeDetectorRef.markForCheck()
      })
  }

  lisaaRiviRekisterituotteesta(rekisterituote: AsiakkaanTuote) {
    this._lisaaRivi(rekisterituote.nimi, rekisterituote)
  }

  lisaaVapaaRivi(nimi: string) {
    this._lisaaRivi(nimi, null)
  }

  private _lisaaRivi(nimi: string, rekisterituote: AsiakkaanTuote | null) {
    combineLatest([this.laskunTuotteetObservable, this.laskunTyyppiObservable, this.alvtObservable])
      .pipe(take(1))
      .subscribe(([tuotteet, tyyppi, alvt]) => {
        const tuote = this.annaUusiTuote(alvt)
        tuote.tallennetaanRekisteriin = !rekisterituote
        if (rekisterituote) {
          tuote.tuote = this.laskuKopioija.copyTuoteLaskuun(rekisterituote)
          tuote.hinta = rekisterituote.hinta ?? null
          if (rekisterituote.yksikko) {
            tuote.yksikko = rekisterituote.yksikko
          }
          if (rekisterituote.verottomuudenPeruste) {
            tuote.verottomuudenPeruste = rekisterituote.verottomuudenPeruste
          }
          const tuotteenAlv = this._annaLaskunAlvTuotteelle(rekisterituote, tyyppi, alvt)
          if (tuotteenAlv) {
            tuote.alv = this.laskuKopioija.flipAlvIfNeededBasedOnDate(tuotteenAlv, this.dateService.currentNumberDate())
          }
        } else {
          tuote.tuote = this.laskuKopioija.copyTuoteLaskuun({ $key: null, nimi: nimi, hinta: null, date: null, alv: null })
        }

        tuotteet.push(tuote)
        // Taulukko päivitetään suoraan, koska asetaArvo ilmoittaisi myös aiempien rivien kenttien kuuntelijoille
        this.parent.tuotteet().value.update(nykyiset => [...nykyiset, this._annaRivinAlkuarvo(tuote, tyyppi)])
        const rivit = [...this.parent.tuotteet]
        this._kuunteleRivia(rivit[rivit.length - 1], tuote, true, tyyppi, alvt)
        this.parent.tuotteet().markAsDirty()

        this.summatMuuttuivat.next()
        this._changeDetectorRef.markForCheck()
      })
  }

  siirrySeuraavalleRiville(indeksi: number) {
    const seuraavaRivi = this.riviKomponentit.toArray()[indeksi + 1]
    if (seuraavaRivi) {
      seuraavaRivi.kohdistaRivinNimikenttaan()
    } else {
      this.uusiRiviHaku?.kohdistaHakukenttaan()
    }
  }

  raahausAlkoi(indeksi: number) {
    this.raahattavaIndeksi = indeksi
  }

  raahausLoppui() {
    this.raahattavaIndeksi = null
    this.raahauksenKohdeIndeksi = null
    this._changeDetectorRef.markForCheck()
  }

  pudotaRivi(kohde: number) {
    const lahde = this.raahattavaIndeksi
    this.raahausLoppui()
    if (lahde === null || lahde === kohde) {
      return
    }
    this._siirraRivi(lahde, kohde)
  }

  private _siirraRivi(lahde: number, kohde: number) {
    this.laskunTuotteetObservable.pipe(take(1)).subscribe(tuotteet => {
      // Rivien oliot siirretään sellaisinaan, jolloin rivien kentät ja niiden kuuntelijat siirtyvät mukana
      this.parent.tuotteet().value.update(nykyiset => {
        const siirretyt = [...nykyiset]
        const [siirrettava] = siirretyt.splice(lahde, 1)
        siirretyt.splice(kohde, 0, siirrettava)
        return siirretyt
      })

      const [tuote] = tuotteet.splice(lahde, 1)
      tuotteet.splice(kohde, 0, tuote)

      const [virhe] = this.virheet.splice(lahde, 1)
      this.virheet.splice(kohde, 0, virhe)
      this.virheetObservable.next(this.virheet)
      this.parent.tuotteet().markAsDirty()

      this.summatMuuttuivat.next()
      this._changeDetectorRef.markForCheck()
    })
  }

  private _lisaaRiviLomakkeeseen(indeksi: number, rivinArvo: LaskunRiviLomake) {
    // Taulukko päivitetään suoraan, koska asetaArvo ilmoittaisi myös muiden rivien kenttien kuuntelijoille
    this.parent.tuotteet().value.update(nykyiset => [...nykyiset.slice(0, indeksi), rivinArvo, ...nykyiset.slice(indeksi)])
  }

  private _annaRivinArvonKopio(arvo: LaskunRiviLomake): LaskunRiviLomake {
    return {
      tuote: arvo.tuote,
      nimi: arvo.nimi,
      tarkenne: arvo.tarkenne,
      ahinta: arvo.ahinta,
      maara: arvo.maara,
      yksikko: arvo.yksikko,
      alv: arvo.alv,
      alvPoissa: arvo.alvPoissa,
      ale: arvo.ale,
      yhteensa: arvo.yhteensa,
      projekti: arvo.projekti,
      verottomuudenPeruste: arvo.verottomuudenPeruste,
      verottomuudenPerusteTarpeen: arvo.verottomuudenPerusteTarpeen,
      tallennetaanRekisteriin: arvo.tallennetaanRekisteriin,
    }
  }

  private annaUusiTuote(alvt: LaskunAlv[]): LaskunTuote {
    const tuote: LaskunTuote = {
      tuote: null,
      hinta: null,
      alv: null,
      ale: null,
      maara: 1,
      yksikko: LASKUN_OLETUSYKSIKKO,
    }

    // Set default alv
    tuote.alv = this._alvService.annaDefaultAlv(alvt, this.dateService.currentNumberDate())
    if (!tuote.alv) {
      tuote.alv = alvt[0]
    }

    return tuote
  }

  private paivitaYhteensa(rivi: FieldTree<LaskunRiviLomake, number>, tuote: LaskunTuote) {
    const uusiYhteensa = this.laskuSharedService.annaLaskurivinYhteensaLaskunTuotteelle(tuote)
    const yhteensaKentta = rivi.yhteensa
    if (this.paivitaNumero(yhteensaKentta().value(), uusiYhteensa)) {
      const formatoitu = this.currencyService.formatoiDesimaali(uusiYhteensa, 2, 'fi')
      const takaisinNumero = this.currencyService.muutaMerkkijonoNumeroksi(formatoitu, 2)
      asetaArvo(yhteensaKentta, takaisinNumero, { ilmoita: false })
    }
  }

  private asetaKappalehinta(tuote: LaskunTuote) {
    delete tuote.hinnanTyyppi
    delete tuote.yhteensa
    delete tuote.verollinenHinta
  }

  private get _hinnatVerollisina(): boolean {
    return !!this.parent.hinnatVerollisina().value() && !this._alvKannatRajattuNollaan
  }

  private _paivitaRivinSummat(rivi: FieldTree<LaskunRiviLomake, number>, tuote: LaskunTuote) {
    if (this._hinnatVerollisina) {
      this._asetaRivinHinnatVerollisesta(tuote)
    } else {
      this.asetaKappalehinta(tuote)
    }
    this.paivitaYhteensa(rivi, tuote)
  }

  private _asetaRivinHinnatVerollisesta(tuote: LaskunTuote) {
    const hinnat = this.laskuSharedService.annaLaskurivinHinnatVerollisesta(tuote)
    if (!hinnat) {
      tuote.hinta = null
      this.asetaKappalehinta(tuote)
      return
    }
    tuote.hinnanTyyppi = 'yhteensa'
    tuote.yhteensa = hinnat.yhteensa
    tuote.hinta = hinnat.hinta
  }

  private _paivitaRivienHinnatVerollisuudenMukaan() {
    this.laskunTuotteetObservable.pipe(take(1)).subscribe(tuotteet => {
      const rivit = [...this.parent.tuotteet]
      for (const [indeksi, rivi] of rivit.entries()) {
        const tuote = tuotteet[indeksi]
        if (this._hinnatVerollisina) {
          tuote.verollinenHinta = this.laskuSharedService.annaLaskurivinVerollinenHinta(tuote)
        }
        this._paivitaRivinSummat(rivi, tuote)
        const naytettavaHinta = this._hinnatVerollisina ? tuote.verollinenHinta : tuote.hinta
        asetaArvo(rivi.ahinta, naytettavaHinta ?? null, { ilmoita: false })
      }
      this.parent.tuotteet().markAsDirty()
      this.summatMuuttuivat.next()
      this._changeDetectorRef.markForCheck()
    })
  }

  private _annaAlvinLukituksenSelite(tyyppi: LaskunTyyppi): AlvinLukituksenSelite | null {
    if (this._alvKannatRajattuNollaan) {
      return { kaannosavain: 'lasku.alv-lukittu-ei-alv-rekisterissa', myynninLaji: tyyppi.avain }
    }
    if (!this._onkoAlvKenttaMuokattavissa(tyyppi)) {
      return { kaannosavain: 'lasku.alv-lukittu-kaannetty-verovelvollisuus', myynninLaji: tyyppi.avain }
    }
    return null
  }

  private _onkoAlvKenttaMuokattavissa(tyyppi: LaskunTyyppi): boolean {
    if (this._alvKannatRajattuNollaan || !tyyppi) {
      return false
    }
    const onkoLajiLukittuNollaan = ALV_NOLLAAN_LUKITUT_LAJIT.includes(tyyppi.avain)
    return !onkoLajiLukittuNollaan
  }

  private _annaRivinAlkuarvo(tuote: LaskunTuote, tyyppi: LaskunTyyppi): LaskunRiviLomake {
    const ahinta = this._hinnatVerollisina ? this.laskuSharedService.annaLaskurivinVerollinenHinta(tuote) : tuote.hinta
    return {
      tuote: this.laskuKopioija.copyTuoteLaskusta(tuote.tuote),
      nimi: tuote.tuote?.nimi ?? '',
      tarkenne: tuote.tarkenne ?? '',
      ahinta: ahinta ?? null,
      maara: tuote.maara ?? null,
      yksikko: tuote.yksikko ?? null,
      alv: tuote.alv ?? null,
      alvPoissa: !this._onkoAlvKenttaMuokattavissa(tyyppi),
      ale: tuote.ale ?? null,
      yhteensa: this.laskuSharedService.annaLaskurivinYhteensaLaskunTuotteelle(tuote),
      projekti: tuote.projekti ?? null,
      verottomuudenPeruste: tuote.verottomuudenPeruste ?? null,
      verottomuudenPerusteTarpeen: this.laskuSharedService.onkoVerottomuudenPerusteTarpeen(tuote.alv),
      tallennetaanRekisteriin: tuote.tallennetaanRekisteriin ?? true,
    }
  }

  // Lomakkeesta poistetun rivin kenttiä ei saa enää lukea, joten kuuntelu lopetetaan ennen seuraavaa muutostunnistusta
  private _lopetaRivinKuuntelu(rivi: FieldTree<LaskunRiviLomake, number>) {
    const lopetus = this._rivienKuuntelunLopetukset.get(rivi)
    this._rivienKuuntelunLopetukset.delete(rivi)
    lopetus?.next()
    lopetus?.complete()
  }

  private _lopetaRivienKuuntelu() {
    for (const rivi of [...this._rivienKuuntelunLopetukset.keys()]) {
      this._lopetaRivinKuuntelu(rivi)
    }
  }

  private _kuunteleRivia(rivi: FieldTree<LaskunRiviLomake, number>, tuote: LaskunTuote, focus: boolean, tyyppi: LaskunTyyppi, alvt: LaskunAlv[]) {
    const lopetus = new Subject<void>()
    this._rivienKuuntelunLopetukset.set(rivi, lopetus)
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(this.ngUnsubscribe), takeUntil(lopetus))

    muutokset(rivi.tuote).subscribe((value: AsiakkaanTuote) => {
      tuote.tuote = this.laskuKopioija.copyTuoteLaskuun(value)
      this.asetaKappalehinta(tuote)
      if (value) {
        if (value.hinta && !rivi.ahinta().value()) {
          asetaArvo(rivi.ahinta, value.hinta)
        }
        if (value.yksikko) {
          asetaArvo(rivi.yksikko, value.yksikko)
        }
        if (value.verottomuudenPeruste) {
          asetaArvo(rivi.verottomuudenPeruste, value.verottomuudenPeruste)
        }
        const alv = this._annaLaskunAlvTuotteelle(value, tyyppi, alvt)
        if (alv) {
          asetaArvo(rivi.alv, this.laskuKopioija.flipAlvIfNeededBasedOnDate(alv, this.dateService.currentNumberDate()))
        } else {
          this._paivitaVerottomuudenPeruste(rivi, rivi.alv().value())
        }
      }
    })
    muutokset(rivi.ahinta).subscribe(value => {
      // console.log('ahinta', value)
      if (this._hinnatVerollisina) {
        tuote.verollinenHinta = value
      } else {
        tuote.hinta = value
      }
      this._paivitaRivinSummat(rivi, tuote)
    })
    muutokset(rivi.maara).subscribe(value => {
      // console.log('maara', value)
      tuote.maara = value
      this._paivitaRivinSummat(rivi, tuote)
    })
    muutokset(rivi.nimi).subscribe(value => {
      const nykyinen = rivi.tuote().value() ?? this.laskuKopioija.copyTuoteLaskusta(null)
      const nimiMuuttuiRekisterituotteesta = !!nykyinen.$key && nykyinen.nimi !== value
      const paivitetty: AsiakkaanTuote = { ...nykyinen, nimi: value }
      if (nimiMuuttuiRekisterituotteesta) {
        paivitetty.$key = null
      }
      asetaArvo(rivi.tuote, paivitetty, { ilmoita: false })
      tuote.tuote = this.laskuKopioija.copyTuoteLaskuun(paivitetty)
    })
    muutokset(rivi.tarkenne).subscribe(value => {
      const siivottu = value?.trim()
      if (siivottu) {
        tuote.tarkenne = siivottu
      } else {
        delete tuote.tarkenne
      }
    })
    muutokset(rivi.yksikko).subscribe(value => {
      tuote.yksikko = value || null
    })
    muutokset(rivi.ale).subscribe(value => {
      // console.log('ale ', value)
      tuote.ale = value === 0 ? null : value
      this._paivitaRivinSummat(rivi, tuote)
    })
    muutokset(rivi.alv).subscribe(value => {
      // console.log('alv ', value)
      const kantaMuuttui = tuote.alv?.tunniste !== value?.tunniste
      tuote.alv = value
      if (kantaMuuttui) {
        this._paivitaRivinSummat(rivi, tuote)
      } else {
        this.paivitaYhteensa(rivi, tuote)
      }
      this._paivitaVerottomuudenPeruste(rivi, value)
    })
    muutokset(rivi.tallennetaanRekisteriin).subscribe(value => {
      tuote.tallennetaanRekisteriin = value
    })
    muutokset(rivi.verottomuudenPeruste).subscribe(value => {
      if (value) {
        tuote.verottomuudenPeruste = value
      } else {
        delete tuote.verottomuudenPeruste
      }
    })
    muutokset(rivi.projekti).subscribe(value => {
      // console.log('projekti ', value)
      tuote.projekti = value
    })
    muutokset(rivi.yhteensa).subscribe(value => {
      // console.log('yhteensa', value)

      tuote.hinnanTyyppi = 'yhteensa'
      tuote.yhteensa = value

      const alvKerroin = this.laskuSharedService.annaAlvKerroin(tuote)
      const aleKerroin = this.laskuSharedService.annaAleKerroin(tuote)
      const rivinYhteensa = new Big(tuote.yhteensa || 0).round(2, Big.roundHalfUp)
      const alentamatonHinta = rivinYhteensa.div(aleKerroin).round(2, Big.roundHalfUp)
      const yhteensaIlmanAlv = alentamatonHinta.div(alvKerroin).round(2, Big.roundHalfUp)
      const maara = new Big(tuote.maara || 1)
      const tuotteenVerotonYksikkohinta = yhteensaIlmanAlv.div(maara)

      // console.log('Ale', aleKerroin.toString(), 'ALV', alvKerroin.toString(), 'Yhteensä', rivinYhteensa.toString())

      const takaisinNumeroYksikkoHinta = this.currencyService.muutaBigDecimalRahaksi(tuotteenVerotonYksikkohinta)
      tuote.hinta = takaisinNumeroYksikkoHinta
      tuote.maara = Number(this.currencyService.muutaBigDecimalRahamerkkijonoksiDesimaalit(maara, 4))

      const hintaKentta = rivi.ahinta
      if (this.paivitaNumero(hintaKentta().value(), tuote.hinta)) {
        const formatoitu = this.currencyService.formatoiDesimaali(tuote.hinta, 2, 'fi')
        const takaisinNumero = this.currencyService.muutaMerkkijonoNumeroksi(formatoitu, 2)
        // console.log('ahintacontrol', hintaControl.value, '!==', takaisinNumero)
        asetaArvo(hintaKentta, takaisinNumero, { ilmoita: false })
      }

      const maaraKentta = rivi.maara
      if (this.paivitaNumero(maaraKentta().value(), tuote.maara)) {
        const formatoitu = this.currencyService.formatoiDesimaali(tuote.maara, 4, 'fi')
        const takaisinNumero = this.currencyService.muutaMerkkijonoNumeroksi(formatoitu, 4)
        // console.log('maaracontrol', maaraControl.value, '!==', takaisinNumero)
        asetaArvo(maaraKentta, takaisinNumero, { ilmoita: false })
      }
    })

    this._paivitaVerottomuudenPeruste(rivi, tuote.alv)

    if (this._hinnatVerollisina) {
      tuote.verollinenHinta = this.laskuSharedService.annaLaskurivinVerollinenHinta(tuote)
      this._paivitaRivinSummat(rivi, tuote)
    }

    if (focus) {
      setTimeout(() => {
        this.riviKomponentit.last.kohdistaRivinTarkennekenttaan()
        this._changeDetectorRef.markForCheck()
      }, 10)
    }
  }

  private _paivitaVerottomuudenPeruste(rivi: FieldTree<LaskunRiviLomake, number>, alv: LaskunAlv) {
    const perusteKentta = rivi.verottomuudenPeruste
    const perusteTarpeen = this.laskuSharedService.onkoVerottomuudenPerusteTarpeen(alv)
    asetaArvo(rivi.verottomuudenPerusteTarpeen, perusteTarpeen, { ilmoita: false })
    if (perusteTarpeen) {
      if (!perusteKentta().value() && this.verottomanMyynninOletusperuste) {
        asetaArvo(perusteKentta, this.verottomanMyynninOletusperuste)
      }
    } else {
      if (perusteKentta().value()) {
        asetaArvo(perusteKentta, null)
      }
    }
  }

  private _annaLaskunAlvTuotteelle(tuote: AsiakkaanTuote | AsiakkaanTuoteLaskussa, tyyppi: LaskunTyyppi, alvt: LaskunAlv[]): LaskunAlv {
    if (tuote && tuote.alv && tuote.alv[tyyppi.avain] && alvt) {
      const alvTunniste = tuote.alv[tyyppi.avain]
      const laskunTyyppi = LaskunTyypit.annaLaskunTyyppi(tyyppi.avain)
      if (alvTunniste && laskunTyyppi) {
        for (const alv of alvt) {
          if (alv.tunniste === alvTunniste) {
            return alv
          }
        }
      }
    }
    return null
  }

  private paivitaNumero(uusi: number, vanha: number): boolean {
    if ((uusi === null || vanha === null) && vanha !== uusi) {
      return true
    }
    if (this.currencyService.onkoNumero(uusi)) {
      if (this.currencyService.onkoNumero(vanha)) {
        const v = new Big(vanha).round(4, Big.roundHalfUp)
        const u = new Big(uusi).round(4, Big.roundHalfUp)
        if (!v.eq(u)) {
          return true
        }
      } else {
        return true
      }
    }
    return false
  }
}
